import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getPaperInsights,
  updateInsight,
  getPaperById,
  updatePaperStatus,
} from "../api/Api";
import { showToast } from "../utils/toastUtils";
import { Container, Row, Col, Stack } from "react-bootstrap";
import PaperInsights from "../components/PaperInsights";
import ChangePaperStatus from "../components/modals/ChangePaperStatus";
import { LuPenSquare } from "react-icons/lu";
import CustomButton from "../components/CustomButton";
import { PostCheck } from "../components/feature/postchecks/Postcheck";

function InsightsAndPostchecks() {
  const { paperId } = useParams();

  return (
    <div className="p-4 px-xl-5">
      <Insights paperId={paperId} />
    </div>
  );
}

const Insights = ({ paperId }) => {
  // Centralized state management
  const [state, setState] = useState({
    paper: null,
    insights: [],
    referenceList: [],
    checkedIssues: {},
    loading: true,
    error: null,
    statusModal: false,
  });

  // State update helper
  const updateState = useCallback((updates) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  }, []);

  // Fetch paper data
  const fetchPaperData = useCallback(async () => {
    if (!paperId) return null;

    try {
      const response = await getPaperById(paperId);

      if (response?.status === 200) {
        updateState({ paper: response.data, error: null });
        return response.data;
      } else {
        updateState({
          error: {
            type: "error",
            message: response?.message || "Failed to fetch paper",
          },
        });
        return null;
      }
    } catch (error) {
      console.error("Error fetching paper data:", error);
      updateState({
        error: { type: "error", message: "Failed to fetch paper data" },
      });
      return null;
    }
  }, [paperId, updateState]);

  // Fetch insights data
  const fetchInsightsData = useCallback(async () => {
    if (!paperId) return;

    try {
      const response = await getPaperInsights(paperId);

      if (response?.status === 200) {
        const insights = response.data.insights_list || [];
        const referenceList = response.data.reference_results || [];

        // Build checked issues map
        const checkedIssues = insights.reduce((acc, issue) => {
          acc[issue.id] = issue.is_marked_as_fixed || false;
          return acc;
        }, {});

        updateState({
          insights,
          referenceList,
          checkedIssues,
          error: null,
        });
      } else if (response?.status === 404) {
        updateState({
          error: { type: "not_found", message: "Paper insights not found" },
        });
      }
    } catch (error) {
      console.error("Error fetching insights:", error);

      if (error.response?.status === 404 || error.status === 404) {
        updateState({
          error: { type: "not_found", message: "Paper insights not found" },
        });
      }
      // Don't set error for insights failure if paper exists
    }
  }, [paperId, updateState]);

  // Initialize all data
  const initializeData = useCallback(async () => {
    if (!paperId) return;

    updateState({ loading: true, error: null });

    // Fetch paper data first
    const paperData = await fetchPaperData();

    // Only fetch insights if paper exists
    if (paperData) {
      await fetchInsightsData();
    }

    updateState({ loading: false });
  }, [paperId, fetchPaperData, fetchInsightsData, updateState]);

  // Handle insight checkbox change
  const handleInsightCheck = useCallback(
    async (insightId, isChecked) => {
      try {
        const payload = {
          insight_id: insightId,
          is_marked_as_fixed: isChecked,
        };

        const response = await updateInsight(paperId, payload);

        if (response?.status === 200) {
          // Update local state optimistically
          updateState({
            checkedIssues: {
              ...state.checkedIssues,
              [insightId]: isChecked,
            },
            insights: state.insights.map((insight) =>
              insight.id === insightId
                ? { ...insight, is_marked_as_fixed: isChecked }
                : insight
            ),
          });
        } else {
          showToast(response?.message || "Failed to update insight", "error");
        }
      } catch (error) {
        console.error("Error updating insight:", error);
        showToast("Failed to update insight", "error");
      }
    },
    [paperId, state.checkedIssues, state.insights, updateState]
  );

  // Handle status change
  const handleStatusChange = useCallback(
    async (statusId) => {
      try {
        const response = await updatePaperStatus(paperId, statusId);

        if (response?.status === 200) {
          // Refresh paper data to get updated status
          await fetchPaperData();
          showToast("Status updated successfully", "success");
        } else {
          showToast(response?.message || "Failed to update status", "error");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        showToast("Failed to update status", "error");
      }

      updateState({ statusModal: false });
    },
    [paperId, fetchPaperData, updateState]
  );

  // Initialize component on mount
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // Loading state
  if (state.loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Error state - Paper not found
  if (state.error?.type === "not_found" || state.error?.type === "error") {
    return (
      <Container className="p-3">
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="text-center">
            <div className="py-5 d-flex flex-column align-items-center justify-content-center">
              <div className="mb-4">
                <i
                  className="bi bi-file-earmark-x"
                  style={{ fontSize: "4rem", color: "#dc3545" }}
                ></i>
              </div>
              <h3 className="mb-3">Paper Not Found</h3>
              <p className="text-muted mb-4">
                The paper you're looking for has been deleted or is no longer
                available. This could happen if the paper was removed from the
                platform or if you don't have permission to access it or may be
                a network error.
              </p>
              <CustomButton onClick={() => window.history.back()}>
                Go Back
              </CustomButton>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // Main content render
  const { paper, insights, statusModal } = state;

  return (
    <>
      {/* Paper Information Header */}
      <Container className="p-3">
        <Row className="align-items-start justify-content-between">
          <Col className="mb-3">
            <h3 className="mb-1 text-capitalize">
              {paper?.name?.replace(".pdf", "") || "Untitled Paper"}
            </h3>
            <p className="text-muted mb-2">
              {paper?.description || "No description available"}
            </p>

            {/* Status Badge */}
            <div className="d-flex my-3 gap-4">
              <Stack
                direction="horizontal"
                gap={3}
                style={{ backgroundColor: "#FFF1C2" }}
                className="py-1 px-3 d-inline-flex flex-row rounded"
              >
                <div className="fw-bold">Status:</div>
                <div className="text-capitalize">
                  {paper?.status || "Unknown"}
                </div>
                <LuPenSquare
                  style={{ cursor: "pointer" }}
                  onClick={() => updateState({ statusModal: true })}
                  size={18}
                  strokeWidth={3}
                  title="Edit status"
                />
              </Stack>
            </div>

            {/* Paper Metadata */}
            <div className="d-flex mb-2">
              <p className="text-muted mb-0 me-1">
                <strong>Project:</strong>
              </p>
              <p className="text-muted mb-0">{paper?.project?.name || "N/A"}</p>
            </div>

            <div className="d-flex mb-2">
              <p className="text-muted mb-0 me-1">
                <strong>WorkStream:</strong>
              </p>
              <p className="text-muted mb-0">
                {paper?.workstream?.name || "N/A"}
              </p>
            </div>

            <div className="d-flex">
              <p className="text-muted mb-0 me-1">
                <strong>Date:</strong>
              </p>
              <p className="text-secondary mb-0">
                {paper?.date_uploaded
                  ? new Date(paper.date_uploaded).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
          </Col>

          {/* Action Buttons */}
          <Col xs="auto" className="d-flex flex-column align-items-end">
            <div className="d-flex gap-2">
              <CustomButton
                variant="outline"
                disabled={!paper?.file_id}
                onClick={() => {
                  if (paper?.file_id) {
                    window.open(
                      `${process.env.REACT_APP_BACKEND_BASE_URL}/media/papers/${paper.file_id}`,
                      "_blank"
                    );
                  }
                }}
                title={
                  !paper?.file_id ? "File not available" : "Download paper"
                }
              >
                Download
              </CustomButton>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Insights Component */}
      <PaperInsights
        handleCheckChange={handleInsightCheck}
        insights={insights}
        paper={paper}
        hideNextButton
        hideExportButton
      />

      {/* Post Checks Component */}
      <Container className="p-3">
        <PostCheck paperId={paperId} />
      </Container>

      {/* Status Change Modal */}
      <ChangePaperStatus
        show={statusModal}
        handleSave={handleStatusChange}
        handleClose={() => updateState({ statusModal: false })}
        refreshPaper={fetchPaperData}
        paperId={paperId}
      />
    </>
  );
};

export default InsightsAndPostchecks;
